/**
 * Historial de versiones estilo Microsoft 365.
 *
 * Cada fichero editable almacena versiones en una carpeta oculta hermana:
 *
 *   <carpeta_usuario>/
 *       miArchivo.xlsx
 *       .iberbox-versions/
 *           miArchivo.xlsx/
 *               2026-04-16T14-32-01.xlsx
 *               2026-04-16T14-45-17.xlsx
 *               ...
 *
 * El prefijo `.iberbox-versions` es el contrato acordado en `web/frontend`
 * para filtrar en el listado (ver `components/files/ListingItem.vue` y
 * `components/files/Listing.vue` si se añade filtrado — pendiente).
 *
 * API:
 *   saveVersionSnapshot({ ibb, parentHandle, originalName, bytes })
 *       → Promise<void>
 *   listVersions({ ibb, parentHandle, originalName })
 *       → Promise<Array<{ handle, name, date }>>
 *   readVersionBytes({ ibb, handle })
 *       → Promise<ArrayBuffer>
 *   deleteVersion({ ibb, handle })
 *       → Promise<void>
 */

import { files as api } from '@/api';

const VERSIONS_ROOT = '.iberbox-versions';

function tsForFilename () {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
}

function splitExt (filename) {
  const i = filename.lastIndexOf('.');
  if (i <= 0) return { base: filename, ext: '' };
  return { base: filename.slice(0, i), ext: filename.slice(i) };
}

async function getChildNode (ibbInstance, parentHandle, childName) {
  // No hay un getChildByName directo; listamos hijos del parent y buscamos.
  const action = await ibbInstance.getChildren(parentHandle);
  if (action && action.error && action.error.type) {
    throw new Error('Error listando hijos: ' + action.error.type);
  }
  const list = (action && action.nodeList) || [];
  for (const n of list) {
    if (n.name === childName) return n;
  }
  return null;
}

async function ensureFolder (ibbInstance, parentHandle, folderName) {
  const existing = await getChildNode(ibbInstance, parentHandle, folderName);
  if (existing) return existing.handle;
  const act = await ibbInstance.createFolder(parentHandle, folderName);
  if (act && act.error && act.error.type) {
    throw new Error('Error creando carpeta ' + folderName + ': ' + act.error.type);
  }
  // Tras createFolder volvemos a localizar el handle
  const justCreated = await getChildNode(ibbInstance, parentHandle, folderName);
  if (!justCreated) throw new Error('Carpeta creada pero no localizable: ' + folderName);
  return justCreated.handle;
}

/**
 * Crea un snapshot del archivo actual dentro de la carpeta oculta de versiones.
 */
export async function saveVersionSnapshot ({ ibb, parentHandle, originalName, bytes }) {
  if (!ibb || !parentHandle || !originalName || !bytes) {
    throw new Error('saveVersionSnapshot: faltan parámetros requeridos');
  }
  const rootHandle = await ensureFolder(ibb, parentHandle, VERSIONS_ROOT);
  const perFileFolder = await ensureFolder(ibb, rootHandle, originalName);
  const { ext } = splitExt(originalName);
  const snapshotName = `${tsForFilename()}${ext}`;
  await api.createFileWithContent(perFileFolder, snapshotName, bytes, ibb);
}

/**
 * Lista las versiones guardadas para un archivo dado. Devuelve orden
 * descendente (la más reciente primero).
 */
export async function listVersions ({ ibb, parentHandle, originalName }) {
  if (!ibb || !parentHandle || !originalName) return [];
  const root = await getChildNode(ibb, parentHandle, VERSIONS_ROOT);
  if (!root) return [];
  const perFile = await getChildNode(ibb, root.handle, originalName);
  if (!perFile) return [];
  const action = await ibb.getChildren(perFile.handle);
  if (action && action.error && action.error.type) return [];
  const nodes = (action && action.nodeList) || [];
  const items = nodes
    .filter(n => n.type === Module.NodeType.FILE.value)
    .map(n => ({
      handle: n.handle,
      name: n.name,
      date: parseNameTimestamp(n.name),
    }))
    .sort((a, b) => (b.date ? b.date.getTime() : 0) - (a.date ? a.date.getTime() : 0));
  return items;
}

/**
 * Descarga los bytes de una versión dada. Reutiliza api.getFileBytes.
 */
export async function readVersionBytes ({ ibb, handle }) {
  return api.getFileBytes(handle, ibb);
}

export async function deleteVersion ({ ibb, handle }) {
  const act = await ibb.deleteNode(handle);
  if (act && act.error && act.error.type) {
    throw new Error('Error borrando versión: ' + act.error.type);
  }
}

function parseNameTimestamp (name) {
  // Formato: YYYY-MM-DDTHH-MM-SS.ext
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2})-(\d{2})-(\d{2})/.exec(name);
  if (!m) return null;
  return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
}

export const __versionsFolderName = VERSIONS_ROOT;
